import React from 'react';
import { Popconfirm } from 'antd';
import { Draggable } from 'react-beautiful-dnd';
import { useTaskDrawer } from '../TaskDrawerContext';

const STATUS_BADGE = {
    'Backlog':       { bg: '#e8edf3', color: '#5e7191', label: 'Backlog' },
    'In Progress':   { bg: '#dbeafe', color: '#1d4ed8', label: 'In Progress' },
    'Under Review':  { bg: '#fef9c3', color: '#854d0e', label: 'Review' },
    'Completed':     { bg: '#dcfce7', color: '#15803d', label: 'Done' },
    'On Hold':       { bg: '#f3e8ff', color: '#7e22ce', label: 'On Hold' },
    'Cancelled':     { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
};

export default function SubtaskItem({ sub, index, onToggle, onDelete }) {
    const { handleOpenSubtask } = useTaskDrawer();
    const isCompleted = sub.status === 'Completed';
    const badge = STATUS_BADGE[sub.status] || STATUS_BADGE['Backlog'];

    return (
        <Draggable draggableId={sub._id} index={index}>
            {(provided) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className="flex items-center gap-2 px-3 py-2.5 bg-white border border-[#e5eaf0] rounded-lg hover:border-[#03488a]/40 hover:shadow-sm transition-all group cursor-pointer"
                    onClick={() => handleOpenSubtask(sub)}
                >
                    {/* Drag handle */}
                    <div
                        {...provided.dragHandleProps}
                        className="text-[#b0bec5] hover:text-[#03488a] cursor-grab active:cursor-grabbing flex items-center shrink-0"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <span className="material-symbols-outlined text-base">drag_indicator</span>
                    </div>

                    {/* Completion checkbox */}
                    <input
                        type="checkbox"
                        checked={isCompleted}
                        onChange={(e) => { e.stopPropagation(); onToggle(sub); }}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded border-[#c5cdd8] text-[#03488a] focus:ring-[#03488a] h-4 w-4 cursor-pointer shrink-0"
                    />

                    {/* Title */}
                    <span
                        className={`flex-1 text-sm font-medium select-none text-left ${
                            isCompleted
                                ? 'line-through text-[#94a3b8]'
                                : 'text-[#1e293b]'
                        }`}
                    >
                        {sub.title}
                    </span>

                    {/* Task ID chip */}
                    {sub.taskId && (
                        <span className="text-[10px] font-mono text-[#94a3b8] shrink-0 hidden group-hover:inline-block">
                            {sub.taskId}
                        </span>
                    )}

                    {/* Status badge */}
                    <span
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0"
                        style={{ background: badge.bg, color: badge.color }}
                    >
                        {badge.label}
                    </span>

                    {/* Open arrow */}
                    <span
                        className="material-symbols-outlined text-base text-[#94a3b8] group-hover:text-[#03488a] transition-colors shrink-0"
                        title="Open subtask"
                    >
                        chevron_right
                    </span>

                    {/* Delete button */}
                    <Popconfirm
                        title="Delete this subtask?"
                        description="This will permanently remove the subtask."
                        onConfirm={(e) => { e?.stopPropagation(); onDelete(sub._id); }}
                        okText="Delete"
                        cancelText="Cancel"
                        placement="topRight"
                        okButtonProps={{ danger: true }}
                    >
                        <button
                            className="text-[#94a3b8] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
                            onClick={(e) => e.stopPropagation()}
                            title="Delete subtask"
                        >
                            <span className="material-symbols-outlined text-base">delete</span>
                        </button>
                    </Popconfirm>
                </div>
            )}
        </Draggable>
    );
}
